import { Link } from 'react-router-dom';
import React from 'react';
import { CurvedBackArrow } from '../../icons/curved-back-arrow';
import { CampaignIcon, LineItemIcon, MediaPlanIcon } from '../layout/nav-icons';
import { CampaignV2 } from 'shared/src/api/campaigns-v2';
import { LineItemTrpcResponse } from 'shared/src/trpc-types/line-item-trpc-types';
import { MediaPlan } from 'shared/src/domain/media-buy/media-buy-types';

export function CampaignBreadcrumbs({ name }: { name: string | undefined }) {
  return (
    <div className="mt-4 flex flex-col">
      <div className="flex items-center text-sm">
        <BreadcrumbLink to="/campaigns" name="Campaigns" isActive={true} />
        <Separator />
      </div>
      <PageName name={name} icon={<CampaignIcon />} type="Campaign" />
    </div>
  );
}

type LineItemBreadcrumbsProps = {
  campaign: CampaignV2;
  name: string | undefined;
};

export function LineItemBreadcrumbsV2({ campaign, name }: LineItemBreadcrumbsProps) {
  return (
    <div className="mt-4 flex flex-col">
      <div className="flex items-center text-sm">
        <BreadcrumbLink to="/campaigns" name="Campaigns" />
        <Separator />
        <BreadcrumbLink
          to={`/campaigns/${campaign.campaign_id}`}
          name={campaign.campaign_name}
          isActive={true}
        />
        <Separator />
      </div>
      <PageName name={name} icon={<LineItemIcon width={32} height={32} />} type="Line Item" />
    </div>
  );
}

type MediaBuyBreadcrumbProps = {
  campaign: CampaignV2;
  lineItem: LineItemTrpcResponse;
  mediaPlan: MediaPlan;
};

export function MediaPlanBreadcrumbs({ campaign, lineItem, mediaPlan }: MediaBuyBreadcrumbProps) {
  return (
    <div className="mt-4 flex flex-col">
      <div className="flex items-center text-sm">
        <BreadcrumbLink to="/campaigns" name="Campaigns" />
        <Separator />
        <BreadcrumbLink
          to={`/campaigns/${campaign.campaign_id}`}
          name={campaign.campaign_name}
          isActive={true}
        />
        <Separator />
        <BreadcrumbLink
          to={`/campaigns/${campaign.campaign_id}/execution/lineitems/${lineItem.id}`}
          name={lineItem.name}
          isActive={true}
        />
        <Separator />
      </div>
      <PageName
        name={mediaPlan.name || mediaPlan.media_buy?.name || 'No Platform Buy'}
        icon={<MediaPlanIcon height={32} width={32} />}
        type="Media Plan"
      />
    </div>
  );
}

type PageNameProps = {
  name: string | undefined;
  icon: React.ReactNode;
  type: string;
};

function PageName({ name, icon, type }: PageNameProps) {
  return (
    <div className="mt-2 flex w-full items-center rounded-sm bg-gray-50 p-1">
      <div className="mr-3 flex items-center rounded-l border-r border-gray-200 bg-gray-100 px-2 py-0">
        <div className="flex h-10 w-10 items-center justify-center">{icon}</div>
        <div className="ml-2 text-sm text-gray-600">{type}</div>
      </div>
      <div
        className="min-w-0 flex-1 truncate pl-2 text-[30px] font-bold text-gray-800"
        title={name}>
        {name ? name : '-'}
      </div>
    </div>
  );
}

type BreadcrumbLinkProps = {
  to: string;
  name: string | undefined;
  isActive?: boolean;
};

function BreadcrumbLink({ to, name, isActive = false }: BreadcrumbLinkProps) {
  if (isActive) {
    return (
      <Link to={to}>
        <div className="flex items-center rounded-sm bg-blue-50 px-2 py-0.5 font-medium text-blue-600">
          <span className="font-medium">{name}</span>
          <div className="ml-2">
            <CurvedBackArrow />
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link className="text-gray-500 hover:underline" to={to}>
      {name}
    </Link>
  );
}

function Separator() {
  return <div className="mx-2 text-gray-950">/</div>;
}
